/**
 * @file /study/:familyId/asks — AI 出力の一覧である。
 *
 * @what: まなぶ人の記録を操作 (AI 解説 / AI 採点) で絞り、新しい順に並べる。対象の名前と絵、
 *   状態、往復の数を1行で出し、選ぶと1件を開いてやりとりと評価を出す。開いている操作は
 *   `?kind=` に持つ。
 * @why: ページを覚えていなくても読み返せる場所が必要である。本だなの上には件数つきのリンクだけを
 *   置き、主役にしない (代表の判断、2026-09-09)。
 * @failure: 所属外 familyId は開けない旨を出す。取得の失敗は1行で知らせる。
 */

import { useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { familyLabel, roleLabel } from "@/features/study/api/families";
import { ASK_PRESET_LABELS } from "@/features/study/api/askRecords";
import { AskRecordPanel } from "@/features/study/guides/AskRecordPanel";
import { LearnerTabs } from "@/features/study/components/LearnerTabs";
import { useLearnerSelection } from "@/features/study/hooks/useLearnerSelection";
import { useMyFamilies } from "@/features/study/hooks/useMyFamilies";
import { useStudyNotes } from "@/features/study/hooks/useStudyNotes";
import { useStudyBackground } from "@/features/study/hooks/useStudyBackground";
import { useHeaderFamily } from "@/shared/ui/headerContext";
import "@/features/study/components/study.css";

/** 一覧に出す操作の並び (押すボタンと同じ言葉、同じ順)。 */
const OPS: readonly { preset: string; label: string }[] = [
  { preset: "ai_explain", label: ASK_PRESET_LABELS.ai_explain! },
  { preset: "ai_grade", label: ASK_PRESET_LABELS.ai_grade! },
];

export function StudyAsksRoute() {
  useStudyBackground();
  const { familyId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const families = useMyFamilies();
  const { learners, learnerId, selectLearner } = useLearnerSelection(familyId);
  const family = useMemo(
    () => (families.status === "ready" ? families.families.find((f) => f.family_id === familyId) : undefined),
    [families, familyId],
  );
  useHeaderFamily(
    family
      ? {
          label: familyLabel(family),
          role: roleLabel(family.role),
          switchable: families.status === "ready" && families.families.length > 1,
        }
      : null,
  );

  const kindParam = params.get("kind");
  const kind = OPS.some((o) => o.preset === kindParam) ? kindParam : null;
  // メモで絞る (メモのペインの「AI 出力 N件」から来る)。題名は一覧から引き、読めなければ id を出す。
  const noteId = params.get("note");
  const notes = useStudyNotes(familyId);
  const noteTitle = noteId ? (notes.find((n) => n.id === noteId)?.title ?? noteId) : null;

  if (families.status === "loading") {
    return (
      <section className="study-scope study-page">
        <p className="study-muted" role="status">
          読み込んでいます...
        </p>
      </section>
    );
  }
  if (families.status === "error" || family === undefined) {
    return (
      <section className="study-scope study-page">
        <p className="study-error" role="alert">
          この家族の学習は開けません。
        </p>
      </section>
    );
  }

  return (
    <section className="study-scope study-page">
      <p className="study-back">
        <Link to={`/study/${encodeURIComponent(familyId)}?view=shelf`}>← 本だなにもどる</Link>
      </p>

      <section className="study-section">
        <h2 className="study-section-title">AI 出力</h2>
        <p className="study-section-help">
          本の紙面や学びメモについて AI に聞いた記録です。答えの下で「役に立った /
          役に立たなかった」を付けられます。
        </p>

        {learners.length > 1 ? (
          <LearnerTabs learners={learners} selectedId={learnerId} onSelect={selectLearner} />
        ) : null}

        {noteId ? (
          <p className="study-ask-filter" role="status">
            メモ「{noteTitle}」について聞いたものだけを出しています。
            <button
              type="button"
              className="study-ghost-btn study-ask-filter-clear"
              onClick={() => {
                params.delete("note");
                setParams(params, { replace: true });
              }}
            >
              すべてのメモにもどす
            </button>
          </p>
        ) : null}

        <div className="study-ask-kinds" role="group" aria-label="操作でしぼりこむ">
          <button
            type="button"
            className={kind === null ? "study-aid-chip is-on" : "study-aid-chip"}
            aria-pressed={kind === null}
            onClick={() => {
              params.delete("kind");
              setParams(params, { replace: true });
            }}
          >
            すべて
          </button>
          {OPS.map((op) => (
            <button
              key={op.preset}
              type="button"
              className={kind === op.preset ? "study-aid-chip is-on" : "study-aid-chip"}
              aria-pressed={kind === op.preset}
              onClick={() => {
                params.set("kind", op.preset);
                setParams(params, { replace: true });
              }}
            >
              {op.label}
            </button>
          ))}
        </div>

        {learnerId === null ? (
          <p className="study-muted">まなぶ人を選ぶと、聞いたことが並びます。</p>
        ) : (
          <AskRecordPanel
            familyId={familyId}
            personId={learnerId}
            hideTitle
            {...(noteId ? { noteIds: [noteId] } : { all: true })}
            {...(kind ? { presetId: kind } : {})}
          />
        )}
      </section>
    </section>
  );
}
